import { parseAgencyDocumentsResponse } from 'app/api/agency-documents/agency-documents-parsers';
import {
  apiAddReportDocument,
  apiDeleteReportDocument,
  apiGetPermitHistory,
  apiGetPermitHistoryFile,
  apiGetPermitHistoryFilesList,
  apiUpdateReportDcraInspection,
} from 'app/api/agency-reports/agency-reports';
import { parseDBAddress } from 'app/api/client-jobs/client-jobs-parsers';
import { normalizeAddressId } from 'app/helpers/normalizeAddressId';
import { HistoryFileItem } from 'app/main/dcra-inspections/inspection-report/permit-history/components/permit-history-item/FileItem';
import { FileInfo } from 'app/ui/components/multi-file-uploader/MultiFileUploader';
import { filterOutSpecialInspectionsAndZoning } from 'app/ui/components/permits-picker/helpers/permitHelpers';
import {
  deleteInspection,
  getInspectionFromStorage,
  saveInspection,
} from 'app/ui/modules/inspection-create/helpers/storageHelpers';
import axios from 'axios';
import { sortBy, uniq } from 'lodash-es';
import moment from 'moment';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AppNS, Dcra } from '../..';
import { apiGetPermitsByAddress, apiGetPermitsById } from '../../api/client-jobs/client-jobs';
import {
  apiSaveInspectionQuestionnaire,
  apiSaveReportChecklist,
  apiSendNewJob,
  apiSubmitReport,
  apiUpdateInspection,
  apiUpdateInspectionStatus,
  getInspection,
  InspectionUpdateAction,
  updateResults,
} from '../../api/dcra-inspections/inspections';
import { AppThunk } from '../index';
import { checkReportValid } from './inspection-reports-validation';

export interface InspectionsState {
  currentInspection: Dcra.Inspection | null;
  newJob: Dcra.Inspection;
  downloadedPermits: Array<AppNS.Permit>;
  isInspectionUpdating: boolean;
  isInspectionUpdated: boolean;
  inspectionUpdateError: AppNS.Params | null;
  checkingInError: AppNS.Params | null;
  isNewJobValid: boolean;
  isNewJobValidChecked: boolean;
  newJobValidErrors: string[];
  isNewJobLoading: boolean;
  isEventUpdateLoading: boolean;
  isNewJobError: string;
  jobSubmitted: string;
  isNewJobLoadingError: boolean;
  isDeleteEventLoading: boolean;
  isDeleteEventError: boolean;
  isPermitsLoadingError: boolean;
  isPermitsLoadingTimeout: boolean;
  isPermitsLoading: boolean;
  isPermitsLoaded: boolean;
  permitsValidErrors: string[];
  foundPermitsAddressesMatched: string[];
  searchPermitsItemsNotFound: string[];
  isPermitsValid: boolean;
  isPermitValidChecked: boolean;
  isPermitDetailsLoadingError: boolean;
  isPermitDetailsLoading: string;
  isTermsAccepted: boolean;
  isInspectionValid: boolean;
  inspectionValidErrors: string[];
  isInspectionValidChecked: boolean;
  isCheckingIn: boolean;

  isInspectionQuestionnaireLoading: boolean;
  isInspectionQuestionnaireLoadingError: AppNS.Params | null;
  isInspectionQuestionnaireSaved: boolean;

  isInspectionChecklistLoading: boolean;
  isInspectionChecklistLoadingError: AppNS.Params | null;
  isInspectionChecklistSaved: boolean;

  isInspectionUpdateLoading: boolean;
  updatingInspectionId: string;
  isInspectionUpdateLoadingError: string[] | null;
  isInspectionUpdateSaved: boolean;

  isReportValid: boolean;
  reportValidErrors: Array<string>;
  isReportValidChecked: boolean;
  isReportFinalizing: boolean;
  detailsActionDone: boolean;
  isReportFinalizingError: AppNS.Params | null;

  permitHistory: { [key: string]: AppNS.PermitHistoryItem[] };
  isHistoryLoading: { [key: string]: boolean };
  isHistoryLoadingError: { [key: string]: boolean };
  isHistoryLoadingTimeout: { [key: string]: boolean };
  historyFiles: { [key: string]: AppNS.Params[] };
  isHistoryFilesLoading: { [key: string]: boolean };
  isHistoryFilesLoadingError: { [key: string]: boolean };
  isHistoryFilesLoadingTimeout: { [key: string]: boolean };

  downloadFiles: { [key: string]: HistoryFileItem };
  isDownloadFilesLoading: { [key: string]: boolean };
  isDownloadFilesLoadingError: { [key: string]: boolean };
  isDownloadFilesLoadingTimeout: { [key: string]: boolean };
}

const emptyJob = {
  id: '',
  address: null,
  inspectorId: '',
  dbAddress: null,
  permits: [],
  pickedPermits: [],
  inspectionGroups: [],
  siteContactName: '',
  siteContactEmail: '',
  siteContactPhone: '',
  date: '',
  searchBy: '',
  permitId: '',
  state: 'new',
  permitNumbers: [],
  addressIds: [],
  timeWindow: null,
} as Dcra.Inspection;

const initialState: InspectionsState = {
  newJob: getInspectionFromStorage() || emptyJob,
  currentInspection: null,
  downloadedPermits: [],
  isNewJobValid: false,
  isInspectionUpdating: false,
  isInspectionUpdated: false,
  inspectionUpdateError: null,
  checkingInError: null,
  isNewJobValidChecked: false,
  newJobValidErrors: [],
  isNewJobLoading: false,
  isEventUpdateLoading: false,
  isNewJobError: '',
  jobSubmitted: '',
  isNewJobLoadingError: false,
  isDeleteEventLoading: false,
  isDeleteEventError: false,
  isPermitsLoadingError: false,
  isPermitsLoadingTimeout: false,
  isPermitsLoading: false,
  isPermitsLoaded: false,
  permitsValidErrors: [],
  foundPermitsAddressesMatched: [],
  searchPermitsItemsNotFound: [],
  isPermitsValid: false,
  isPermitValidChecked: false,
  isPermitDetailsLoadingError: false,
  isPermitDetailsLoading: '',
  isTermsAccepted: false,
  inspectionValidErrors: [],
  isInspectionValid: false,
  isInspectionValidChecked: false,
  isCheckingIn: false,
  isInspectionQuestionnaireLoading: false,
  isInspectionQuestionnaireLoadingError: null,
  isInspectionQuestionnaireSaved: false,
  isInspectionChecklistLoading: false,
  isInspectionChecklistLoadingError: null,
  isInspectionChecklistSaved: false,
  updatingInspectionId: '',
  isInspectionUpdateLoadingError: null,
  isInspectionUpdateLoading: false,
  isInspectionUpdateSaved: false,
  isReportValid: false,
  reportValidErrors: [],
  isReportFinalizing: false,
  isReportFinalizingError: null,
  isReportValidChecked: false,
  detailsActionDone: false,

  // PERMIT HISTORY
  permitHistory: {},
  isHistoryLoading: {},
  isHistoryLoadingError: {},
  isHistoryLoadingTimeout: {},

  historyFiles: {},
  isHistoryFilesLoading: {},
  isHistoryFilesLoadingError: {},
  isHistoryFilesLoadingTimeout: {},

  downloadFiles: {},
  isDownloadFilesLoading: {},
  isDownloadFilesLoadingError: {},
  isDownloadFilesLoadingTimeout: {},
};

function parseEventInspectionsFiles(permits: AppNS.Permit[]): AppNS.Permit[] {
  return permits.map((permit: AppNS.Permit) => {
    permit.inspections = permit.inspections?.map((inspection: AppNS.Inspection) => {
      return {
        ...inspection,
        files: parseAgencyDocumentsResponse(inspection.documents || []),
      };
    }) as AppNS.Inspection[];
    return permit;
  });
}

function checkPermitInspections(permits: Array<AppNS.PickedPermit>): string[] {
  let result: string[] = [];
  permits.forEach((permit) => {
    if (permit.isOn && permit.inspections.length === 0) {
      result.push(permit.id.toUpperCase());
    }
  });
  return result;
}

function checkPermits(permits: Array<AppNS.PickedPermit>): boolean {
  let result = false;
  permits.forEach((permit) => {
    if (permit.isOn) {
      result = true;
    }
  });
  return result;
}

function ifPickedPermitsValid(state: InspectionsState) {
  const job = state.newJob;

  const validationErrors = [];

  if (!state.isTermsAccepted) {
    validationErrors.push('Project terms must be accepted.');
  }

  if (!checkPermits(job.pickedPermits)) {
    validationErrors.push('Please pick some permits');
  }

  const checkPermitInspectionsResult = checkPermitInspections(job.pickedPermits);
  if (checkPermitInspectionsResult.length) {
    validationErrors.push(
      `There cannot be checked permit without picked inspection. Please pick some inspection for permits: ${checkPermitInspectionsResult.join(
        ', '
      )}`
    );
  }

  state.permitsValidErrors = validationErrors;
  state.isPermitsValid = validationErrors.length ? false : true;
}

function ifInspectionValid(state: InspectionsState) {
  const inspection = state.newJob;

  const validationErrors = [];

  if (!inspection.inspectorId) {
    validationErrors.push('Please pick an Inspector.');
  }

  if (!inspection.date) {
    validationErrors.push('Please select an available date from the calendar.');
  }

  state.inspectionValidErrors = validationErrors;
  state.isInspectionValid = validationErrors.length ? false : true;
}

function checkNotFoundPermits(permits: Array<AppNS.Permit>, request: string): string[] {
  const requestArray = request.split(',');
  return requestArray.filter((requestItem: string) => {
    const trimmed = requestItem.trim();
    return (
      permits.filter((permit: AppNS.Permit) => permit.permitNumber.toLowerCase() === trimmed.toLowerCase()).length === 0
    );
  });
}

const inspections = createSlice({
  name: 'inspections',
  initialState,
  reducers: {
    setCurrentInspectionSwoState(state: InspectionsState, action: PayloadAction<Dcra.Inspection>) {
      if (state.currentInspection?.id === action.payload.id) {
        state.currentInspection.state = action.payload.state;
      }
    },
    setNewJobAddress(state: InspectionsState, action: PayloadAction<AppNS.ProjectAddress | null>) {
      state.newJob.address = action.payload;
      saveInspection(state.newJob);
    },
    setNewJobPermitId(state: InspectionsState, action: PayloadAction<string>) {
      state.newJob.permitId = action.payload;
      saveInspection(state.newJob);
    },
    setSearchByType(state: InspectionsState, action: PayloadAction<AppNS.ProjectSearchByType>) {
      state.newJob.searchBy = action.payload;
      saveInspection(state.newJob);
    },
    setNewJobPermits(state: InspectionsState, action: PayloadAction<Array<AppNS.Permit>>) {
      state.newJob.permits = action.payload;
    },
    setRequirements(state: InspectionsState, action: PayloadAction<AppNS.Params>) {
      state.newJob = { ...state.newJob, ...action.payload };
    },
    setIsNewJobValidChecked(state: InspectionsState) {
      state.isNewJobValidChecked = true;
    },
    sendNewJobStart(state: InspectionsState, action: PayloadAction<Dcra.Inspection>) {
      state.isNewJobError = '';
      state.isNewJobLoading = true;
      state.newJob = action.payload;
      saveInspection(state.newJob);
    },
    sendNewJobSuccess(state: InspectionsState, action: PayloadAction<string>) {
      state.isNewJobLoading = false;
      state.newJob.id = action.payload;

      // save to localStorage with new id
      saveInspection(state.newJob);
    },
    sendNewJobFailed(state: InspectionsState, action: PayloadAction<string>) {
      state.isNewJobLoading = false;
      state.isNewJobError = action.payload;
    },
    setSubmitState(state: InspectionsState, action: PayloadAction<string>) {
      state.jobSubmitted = action.payload;
    },
    getActionFailed(state: InspectionsState) {
      state.isNewJobLoading = false;
    },
    resetJob(state: InspectionsState) {
      state.newJob = emptyJob;
      state.isTermsAccepted = false;
      deleteInspection();
    },
    resetPermitsValidation(state: InspectionsState) {
      state.permitsValidErrors = [];
      state.isPermitsValid = false;
      state.isPermitValidChecked = false;
    },
    getJobStart(state: InspectionsState) {
      state.isNewJobLoading = true;
      state.isInspectionUpdating = false;
      state.inspectionUpdateError = null;
      state.detailsActionDone = false;
      state.isInspectionQuestionnaireSaved = false;
      state.isInspectionQuestionnaireLoadingError = null;
      state.isInspectionQuestionnaireSaved = false;
      state.isInspectionChecklistLoadingError = null;
      state.isInspectionChecklistSaved = false;
      state.isInspectionUpdateLoadingError = null;
      state.isInspectionUpdateSaved = false;
      state.isReportFinalizing = false;
      state.isReportFinalizingError = null;
    },
    getJobFailed(state: InspectionsState) {
      state.isNewJobLoading = false;
      state.isNewJobLoadingError = true;
    },
    getJobSuccess(state: InspectionsState, action: PayloadAction<Dcra.Inspection>) {
      state.isNewJobLoadingError = false;
      state.isNewJobLoading = false;
      state.currentInspection = {
        ...action.payload,
        permits: parseEventInspectionsFiles(action.payload.permits),
      };
      checkReportValid(state);
    },
    getEventUpdateStart(state: InspectionsState) {
      state.isEventUpdateLoading = true;
    },
    getEventUpdateFailed(state: InspectionsState) {
      state.isEventUpdateLoading = false;
    },
    getEventUpdateSuccess(state: InspectionsState, action: PayloadAction<Dcra.Inspection>) {
      state.isEventUpdateLoading = false;
      state.currentInspection = action.payload;
    },
    getPermitsByAddressStart(state: InspectionsState) {
      state.searchPermitsItemsNotFound = [];
      state.foundPermitsAddressesMatched = [];
      state.isPermitsLoadingTimeout = false;
      state.isPermitsLoadingError = false;
      state.isPermitsLoading = true;
      state.isPermitsLoaded = false;
    },
    getPermitsByAddressFailed(state: InspectionsState) {
      state.isPermitsLoading = false;
      state.isPermitsLoadingError = true;
    },
    getPermitsByAddressSuccess(
      state: InspectionsState,
      action: PayloadAction<{ state: string; results: Array<AppNS.Permit> }>
    ) {
      if (action.payload.state === 'completed') {
        state.isPermitsLoadingError = false;
        state.isPermitsLoading = false;
        // stop polling
        state.isPermitsLoaded = true;
        state.newJob.permits = sortBy(action.payload.results, (res) =>
          moment(res.recStatusDate, 'MM/DD/YYYY')
        ).reverse();
        // save all possible unique address strings from permits for this address
        const addressIds = action.payload.results.map((item: AppNS.Permit) => item.addressId);
        state.newJob.addressIds = uniq(addressIds) as string[];
        ifPickedPermitsValid(state);
        state.newJob.cluster = action.payload.results.find((item: AppNS.Permit) => item.cluster)?.cluster;
      } else if (action.payload.state === 'timeout') {
        state.isPermitsLoadingTimeout = true;
        state.isPermitsLoading = false;
        // stop polling
        state.isPermitsLoaded = true;
      }
    },
    getPermitByIdStart(state: InspectionsState) {
      state.foundPermitsAddressesMatched = [];
      state.searchPermitsItemsNotFound = [];
      state.isPermitsLoadingError = false;
      state.isPermitsLoadingTimeout = false;
      state.isPermitsLoading = true;
      state.newJob.dbAddress = null;
    },
    getPermitByIdFailed(state: InspectionsState) {
      state.isPermitsLoading = false;
      state.isPermitsLoadingError = true;
    },
    getPermitByIdSuccess(state: InspectionsState, action: PayloadAction<Array<AppNS.Permit>>) {
      state.isPermitsLoadingError = false;
      state.isPermitsLoadingTimeout = false;
      state.isPermitsLoading = false;
      state.newJob.permits = action.payload.map((permit: AppNS.Permit) => {
        return {
          ...permit,
          inspections: filterOutSpecialInspectionsAndZoning(permit.inspections) || [],
        };
      });
      if (action.payload.length > 0) {
        const addressIds = action.payload.map((item: AppNS.Permit) => normalizeAddressId(item.addressId));
        state.newJob.addressIds = uniq(addressIds);
        state.foundPermitsAddressesMatched = state.newJob.addressIds;
        if (state.foundPermitsAddressesMatched.length > 1) {
          state.newJob.dbAddress = null;
        } else {
          state.newJob.dbAddress = parseDBAddress(action.payload[0].addressInfo);
        }
        state.newJob.cluster = action.payload.find((item: AppNS.Permit) => item.cluster)?.cluster;
        state.searchPermitsItemsNotFound = checkNotFoundPermits(action.payload, state.newJob.permitId);
      }
      ifPickedPermitsValid(state);
    },
    resetPickedPermits(state: InspectionsState) {
      state.newJob.pickedPermits = [];
    },
    storeDownloadedPermits(state: InspectionsState, action: PayloadAction<AppNS.Permit>) {
      state.downloadedPermits.push(action.payload);
    },
    savePermitDetails(state: InspectionsState, action: PayloadAction<AppNS.Permit>) {
      state.isPermitDetailsLoadingError = false;
      state.isPermitDetailsLoading = '';
      state.newJob.permits = state.newJob.permits.map((item) => {
        if (action.payload.id === item.id) {
          return { ...item, ...action.payload };
        }
        return item;
      });
      ifPickedPermitsValid(state);
      saveInspection(state.newJob);
    },
    setPickedPermit(state: InspectionsState, action: PayloadAction<AppNS.PickedPermit>) {
      let currentPickedPermits = state.newJob.pickedPermits;
      if (currentPickedPermits) {
        const result = currentPickedPermits.find((item) => item.id === action.payload.id);
        if (!result) {
          currentPickedPermits.push(action.payload);
        } else {
          currentPickedPermits = currentPickedPermits.map((item) => {
            if (item.id === action.payload.id) {
              return action.payload;
            }
            return item;
          });
        }
        state.newJob.pickedPermits = currentPickedPermits;
        saveInspection(state.newJob);
        ifPickedPermitsValid(state);
      }
    },
    setIsPermitValidChecked(state: InspectionsState) {
      state.isPermitValidChecked = true;
    },
    setIsInspectionValidChecked(state: InspectionsState) {
      state.isInspectionValidChecked = true;
    },
    setTerms(state: InspectionsState, action: PayloadAction<boolean>) {
      state.isTermsAccepted = action.payload;
      ifPickedPermitsValid(state);
    },
    checkNewJob(state: InspectionsState) {
      ifPickedPermitsValid(state);
    },
    setAdditionalInfo(state: InspectionsState, action: PayloadAction<Partial<Dcra.Inspection>>) {
      state.newJob = {
        ...state.newJob,
        ...action.payload,
      };

      saveInspection(state.newJob);
      ifInspectionValid(state);
    },
    inspectionValidationInit(state: InspectionsState) {
      ifInspectionValid(state);
    },
    updateInspectionStart(state: InspectionsState) {
      state.isInspectionUpdating = true;
      state.isInspectionUpdated = false;
    },
    CheckingInStart(state: InspectionsState) {
      state.isCheckingIn = true;
    },
    updateInspectionSuccess(state: InspectionsState, action: PayloadAction<AppNS.Params>) {
      state.isInspectionUpdating = false;
      state.isInspectionUpdated = true;
      state.inspectionUpdateError = null;
      state.checkingInError = null;
      state.isCheckingIn = false;
      state.currentInspection = {
        ...state.currentInspection,
        ...action.payload.properties,
      };
      checkReportValid(state);
    },
    updateInspectionFailure(state: InspectionsState, action: PayloadAction<AppNS.Params>) {
      state.isInspectionUpdating = false;
      state.isInspectionUpdated = false;
      state.inspectionUpdateError = action.payload;
    },
    checkingInFailure(state: InspectionsState, action: PayloadAction<AppNS.Params>) {
      state.isCheckingIn = false;
      state.checkingInError = action.payload;
    },
    resetUpdating(state: InspectionsState) {
      state.isInspectionUpdating = false;
      state.inspectionUpdateError = null;
      state.detailsActionDone = false;
      state.isInspectionQuestionnaireSaved = false;
      state.isInspectionQuestionnaireLoadingError = null;
      state.isInspectionQuestionnaireSaved = false;
      state.isInspectionChecklistLoadingError = null;
      state.isInspectionChecklistSaved = false;
      state.isInspectionUpdateLoadingError = null;
      state.isInspectionUpdateSaved = false;
      state.isReportFinalizing = false;
      state.isReportFinalizingError = null;
    },
    addFile(state: InspectionsState, action: PayloadAction<{ inspectionEventId: string; file: FileInfo }>) {
      if (state.currentInspection) {
        state.currentInspection.permits = state.currentInspection.permits.map((permit: AppNS.Permit) => {
          permit.inspections = permit.inspections?.map((inspection: AppNS.Inspection) => {
            if (inspection.id === action.payload.inspectionEventId) {
              return {
                ...inspection,
                files: [...(inspection.files || []), action.payload.file],
              };
            } else {
              return inspection;
            }
          }) as AppNS.Inspection[];
          return permit;
        });
      }
    },
    updateFiles(state: InspectionsState, action: PayloadAction<{ inspectionEventId: string; files: FileInfo[] }>) {
      if (state.currentInspection) {
        state.currentInspection.permits = state.currentInspection.permits.map((permit: AppNS.Permit) => {
          permit.inspections = permit.inspections?.map((inspection: AppNS.Inspection) => {
            if (inspection.id === action.payload.inspectionEventId) {
              return {
                ...inspection,
                files: action.payload.files,
              };
            } else {
              return inspection;
            }
          }) as AppNS.Inspection[];
          return permit;
        });
      }
    },

    setInspectionQuestionnaireStart(state: InspectionsState) {
      if (state.currentInspection) {
        state.isInspectionQuestionnaireLoadingError = null;
        state.isInspectionQuestionnaireLoading = true;
        state.isInspectionQuestionnaireSaved = false;
      }
    },
    setInspectionQuestionnaireFailed(state: InspectionsState, action: PayloadAction<AppNS.Params>) {
      if (state.currentInspection) {
        state.isInspectionQuestionnaireLoading = false;
        state.isInspectionQuestionnaireLoadingError = action.payload;
        state.isInspectionQuestionnaireSaved = false;
      }
    },
    setInspectionQuestionnaireSuccess(state: InspectionsState, action: PayloadAction<AppNS.Params>) {
      if (state.currentInspection) {
        state.isInspectionQuestionnaireLoading = false;
        state.isInspectionQuestionnaireLoadingError = null;
        state.currentInspection = {
          ...state.currentInspection,
          ...action.payload.properties,
          permits: parseEventInspectionsFiles(action.payload.properties.permits),
        };
        state.isInspectionQuestionnaireSaved = true;
      }
      checkReportValid(state);
    },
    setInspectionChecklistStart(state: InspectionsState) {
      if (state.currentInspection) {
        state.isInspectionChecklistLoadingError = null;
        state.isInspectionChecklistLoading = true;
        state.isInspectionChecklistSaved = false;
      }
    },
    setInspectionChecklistFailed(state: InspectionsState, action: PayloadAction<string[]>) {
      if (state.currentInspection) {
        state.isInspectionChecklistLoading = false;
        state.isInspectionChecklistLoadingError = action.payload;
        state.isInspectionChecklistSaved = false;
      }
    },
    setInspectionChecklistSuccess(state: InspectionsState, action: PayloadAction<AppNS.Params>) {
      if (state.currentInspection) {
        state.isInspectionChecklistLoading = false;
        state.isInspectionChecklistLoadingError = null;
        state.currentInspection = {
          ...state.currentInspection,
          ...action.payload.properties,
        };
        state.isInspectionChecklistSaved = true;
      }
      checkReportValid(state);
    },
    resetUpdatingChecklist(state: InspectionsState) {
      if (state.currentInspection) {
        state.isInspectionChecklistLoading = false;
        state.isInspectionChecklistLoadingError = null;
        state.isInspectionChecklistSaved = false;
      }
    },
    setInspectionUpdateStart(state: InspectionsState, action: PayloadAction<string>) {
      if (state.currentInspection) {
        state.updatingInspectionId = action.payload;
        state.isInspectionUpdateLoadingError = null;
        state.isInspectionUpdateLoading = true;
        state.isInspectionUpdateSaved = false;
      }
    },
    setInspectionUpdateFailed(state: InspectionsState, action: PayloadAction<string[]>) {
      if (state.currentInspection) {
        state.isInspectionUpdateLoading = false;
        state.isInspectionUpdateLoadingError = action.payload;
        state.isInspectionUpdateSaved = false;
      }
    },
    setInspectionUpdateSuccess(
      state: InspectionsState,
      action: PayloadAction<{
        id: string;
        notes: string;
        state: AppNS.InspectionStatus;
        failedCodes: string[];
        customFailedCodes: string;
        gsComments: string;
        recordedAmperage: string;
        electricalSupplementalType: string;
      }>
    ) {
      if (state.currentInspection) {
        state.isInspectionUpdateLoading = false;
        state.isInspectionUpdateLoadingError = null;
        state.isInspectionUpdateSaved = true;

        state.currentInspection.permits = state.currentInspection.permits.map((permit: AppNS.Permit) => {
          permit.inspections = permit.inspections?.map((inspection: AppNS.Inspection) => {
            if (inspection.id === action.payload.id) {
              return {
                ...inspection,
                notes: action.payload.notes,
                status: action.payload.state,
                failedCodes: action.payload.failedCodes,
                customFailedCodes: action.payload.customFailedCodes,
                gsComments: action.payload.gsComments,
                recordedAmperage: action.payload.recordedAmperage,
                electricalSupplementalType: action.payload.electricalSupplementalType,
              };
            } else {
              return inspection;
            }
          }) as AppNS.Inspection[];
          return permit;
        });
        checkReportValid(state);
      }
    },
    setFinalizeStart(state: InspectionsState) {
      state.isReportFinalizing = true;
      state.isReportFinalizingError = null;
    },
    setSubmitSuccess(state: InspectionsState, action: PayloadAction<AppNS.Params>) {
      state.isReportFinalizing = false;
      state.isReportFinalizingError = null;
      state.isReportValidChecked = false;
      state.reportValidErrors = [];
      if (state.currentInspection) {
        state.currentInspection = {
          ...state.currentInspection,
          ...action.payload.properties,
        };
      }
    },
    setFinalizeFailed(state: InspectionsState, action: PayloadAction<AppNS.Params>) {
      state.isReportFinalizing = false;
      state.isReportFinalizingError = action.payload;
    },
    setIsReportValidChecked(state: InspectionsState) {
      state.isReportValidChecked = true;
    },
    setDetailsActionDone(state: InspectionsState, action: PayloadAction<boolean>) {
      state.detailsActionDone = action.payload;
    },
    removeImageStart(state: InspectionsState, action: PayloadAction<{ inspectionId: string; imageId: string }>) {
      if (state.currentInspection) {
        state.currentInspection.permits = state.currentInspection.permits.map((permit: AppNS.Permit) => {
          permit.inspections = permit.inspections?.map((inspection: AppNS.Inspection) => {
            if (inspection.id === action.payload.inspectionId) {
              return {
                ...inspection,
                files: inspection.files?.map((file: FileInfo) =>
                  file.id === action.payload.imageId ? { ...file, isLoaded: false } : file
                ),
              };
            } else {
              return inspection;
            }
          }) as AppNS.Inspection[];
          return permit;
        });
      }
    },
    removeImageSuccess(state: InspectionsState, action: PayloadAction<{ inspectionId: string; imageId: string }>) {
      if (state.currentInspection) {
        state.currentInspection.permits = state.currentInspection.permits.map((permit: AppNS.Permit) => {
          permit.inspections = permit.inspections?.map((inspection: AppNS.Inspection) => {
            if (inspection.id === action.payload.inspectionId) {
              return {
                ...inspection,
                files: inspection.files?.map((file: FileInfo) =>
                  file.id === action.payload.imageId ? { ...file, isRemoved: true } : file
                ),
              };
            } else {
              return inspection;
            }
          }) as AppNS.Inspection[];
          return permit;
        });
      }
    },
    getPermitHistoryStart(state: InspectionsState, action: PayloadAction<string>) {
      state.isHistoryLoading[action.payload] = true;
      state.isHistoryLoadingError[action.payload] = false;
      state.isHistoryLoadingTimeout[action.payload] = false;
    },
    getPermitHistoryFailed(state: InspectionsState, action: PayloadAction<string>) {
      state.isHistoryLoading[action.payload] = false;
      state.isHistoryLoadingError[action.payload] = true;
    },
    getPermitsHistoryTimeoutFailed(state: InspectionsState, action: PayloadAction<string>) {
      state.isHistoryLoading[action.payload] = false;
      state.isHistoryLoadingError[action.payload] = true;
      state.isHistoryLoadingTimeout[action.payload] = true;
    },
    getPermitsHistorySuccess(
      state: InspectionsState,
      action: PayloadAction<{ id: string; history: AppNS.PermitHistoryItem[] }>
    ) {
      state.isHistoryLoading[action.payload.id] = false;
      state.permitHistory[action.payload.id] = action.payload.history;
    },
    getHistoryFilesStart(state: InspectionsState, action: PayloadAction<string>) {
      state.isHistoryFilesLoading[action.payload] = true;
      state.isHistoryFilesLoadingError[action.payload] = false;
      state.isHistoryFilesLoadingTimeout[action.payload] = false;
    },
    getHistoryFilesFailed(state: InspectionsState, action: PayloadAction<string>) {
      state.isHistoryFilesLoading[action.payload] = false;
      state.isHistoryFilesLoadingError[action.payload] = true;
    },
    getHistoryFilesTimeoutFailed(state: InspectionsState, action: PayloadAction<string>) {
      state.isHistoryFilesLoading[action.payload] = false;
      state.isHistoryFilesLoadingError[action.payload] = true;
      state.isHistoryFilesLoadingTimeout[action.payload] = true;
    },
    getHistoryFilesSuccess(state: InspectionsState, action: PayloadAction<{ id: string; files: AppNS.Params[] }>) {
      state.isHistoryFilesLoading[action.payload.id] = false;
      state.historyFiles[action.payload.id] = action.payload.files;
    },

    getDownloadFileStart(state: InspectionsState, action: PayloadAction<HistoryFileItem>) {
      state.isDownloadFilesLoading[action.payload.id] = true;
      state.isDownloadFilesLoadingError[action.payload.id] = false;
      state.isDownloadFilesLoadingTimeout[action.payload.id] = false;
      state.downloadFiles[action.payload.id] = action.payload;
    },
    getDownloadFileTimeoutFailed(state: InspectionsState, action: PayloadAction<string>) {
      state.isDownloadFilesLoading[action.payload] = false;
      state.isDownloadFilesLoadingError[action.payload] = true;
      state.isDownloadFilesLoadingTimeout[action.payload] = true;
    },
    resetDownloadFileTimeout(state: InspectionsState, action: PayloadAction<string>) {
      state.isDownloadFilesLoading[action.payload] = false;
      state.isDownloadFilesLoadingError[action.payload] = false;
      state.isDownloadFilesLoadingTimeout[action.payload] = false;
    },
    getDownloadFileSuccess(state: InspectionsState, action: PayloadAction<{ id: string; doc: string }>) {
      state.isDownloadFilesLoading[action.payload.id] = false;
      state.downloadFiles[action.payload.id].document = action.payload.doc;
    },
  },
});

export const {
  setCurrentInspectionSwoState,
  checkNewJob,
  getActionFailed,
  getJobFailed,
  getJobStart,
  getJobSuccess,
  getEventUpdateStart,
  getEventUpdateFailed,
  getEventUpdateSuccess,
  getPermitsByAddressFailed,
  getPermitsByAddressStart,
  getPermitsByAddressSuccess,
  getPermitByIdFailed,
  getPermitByIdStart,
  getPermitByIdSuccess,
  savePermitDetails,
  resetJob,
  resetPickedPermits,
  sendNewJobStart,
  sendNewJobSuccess,
  sendNewJobFailed,
  setAdditionalInfo,
  setIsNewJobValidChecked,
  setIsPermitValidChecked,
  setNewJobAddress,
  setNewJobPermitId,
  setNewJobPermits,
  setPickedPermit,
  setTerms,
  setRequirements,
  setSubmitState,
  setSearchByType,
  resetPermitsValidation,
  setIsInspectionValidChecked,
  inspectionValidationInit,
  updateInspectionStart,
  updateInspectionSuccess,
  updateInspectionFailure,
  resetUpdating,
  storeDownloadedPermits,
  CheckingInStart,
  checkingInFailure,
  addFile,
  setInspectionQuestionnaireStart,
  setInspectionQuestionnaireFailed,
  setInspectionQuestionnaireSuccess,
  setInspectionChecklistStart,
  setInspectionChecklistFailed,
  setInspectionChecklistSuccess,
  resetUpdatingChecklist,
  setInspectionUpdateStart,
  setInspectionUpdateFailed,
  setInspectionUpdateSuccess,
  setFinalizeStart,
  setSubmitSuccess,
  setFinalizeFailed,
  setIsReportValidChecked,
  setDetailsActionDone,
  removeImageStart,
  removeImageSuccess,
  updateFiles,
  // PERMIT HISTORY
  getPermitHistoryStart,
  getPermitHistoryFailed,
  getPermitsHistorySuccess,
  getPermitsHistoryTimeoutFailed,
  //
  getHistoryFilesStart,
  getHistoryFilesFailed,
  getHistoryFilesTimeoutFailed,
  getHistoryFilesSuccess,
  //
  getDownloadFileStart,
  getDownloadFileTimeoutFailed,
  getDownloadFileSuccess,
  resetDownloadFileTimeout,
} = inspections.actions;

export default inspections.reducer;

export const sendJob =
  (inspection: Dcra.Inspection): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(sendNewJobStart(inspection));
      const response = await apiSendNewJob(inspection);
      dispatch(sendNewJobSuccess(response));
      dispatch(setSubmitState(response));
    } catch (err: any) {
      if (err) {
        dispatch(sendNewJobFailed(err as string));
      }
    }
  };

export const getCurrentInspection =
  (id: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getJobStart());
      const response = await getInspection(id);
      dispatch(getJobSuccess(response));
    } catch (err: any) {
      if (err) {
        dispatch(getJobFailed());
      }
    }
  };

export const updateInspectionResults =
  (id: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getEventUpdateStart());
      const response = await updateResults({ id });
      dispatch(getEventUpdateSuccess(response));
    } catch (err: any) {
      if (err) {
        dispatch(getEventUpdateFailed());
      }
    }
  };

export const updateInspection =
  (id: string, action: InspectionUpdateAction, params: AppNS.Params): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(updateInspectionStart());
      const response = await apiUpdateInspection(id, action, params);
      dispatch(updateInspectionSuccess(response));
    } catch (err: any) {
      if (err) {
        dispatch(updateInspectionFailure(err));
      }
    }
  };

export const updateInspectionChecking =
  (
    id: string,
    action: 'check_in' | 'check_out',
    params: {
      previousId?: string;
      lat: string;
      lng: string;
      unsafeConditions?: boolean;
      unsafeConditionsComment?: string;
      localizationError?: string;
    }
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(CheckingInStart());
      let prevResponse, response;
      if (params.previousId) {
        prevResponse = await apiUpdateInspectionStatus(params.previousId, 'check_out', params);
        if (prevResponse?.id) {
          response = await apiUpdateInspectionStatus(id, action, params);
        }
      } else {
        response = await apiUpdateInspectionStatus(id, action, params);
      }
      if (response?.createdAt) {
        dispatch(
          updateInspectionSuccess({
            properties: {
              ...(action === 'check_in'
                ? {
                    checkInAt: response?.createdAt,
                    state: 'in_progress',
                  }
                : {
                    checkOutAt: response?.createdAt,
                  }),
            },
          })
        );
      }
    } catch (err: any) {
      if (err) {
        dispatch(checkingInFailure(err as AppNS.Params));
      }
    }
  };

export const updateInspectionPermits =
  (id: string, params: AppNS.Params, forceDefaultStatus: string | undefined = undefined): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(updateInspectionStart());
      const response = await apiUpdateInspection(id, 'update-permits', params);
      if (response.id) {
        dispatch(
          updateInspectionSuccess({
            properties: {
              permits: params.permits.map((item: any) => {
                return {
                  permitNumber: item.permit_number,
                  inspections: item.inspection_types.map((inspection: any) => {
                    return {
                      inspectionType: inspection,
                      ...(forceDefaultStatus && { status: forceDefaultStatus }),
                    };
                  }),
                };
              }),
            },
          })
        );
      }
    } catch (err: any) {
      if (err) {
        dispatch(updateInspectionFailure(err));
      }
    }
  };

export const getPermitsByAddress =
  (address: AppNS.ProjectAddress): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getPermitsByAddressStart());
      const result = await apiGetPermitsByAddress(address);

      dispatch(
        getPermitsByAddressSuccess({
          results: result.results,
          state: result.state,
        })
      );
    } catch (err: any) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err);
      dispatch(getPermitsByAddressFailed());
    }
  };

export const getPermitsById =
  (ids: string, jobType: AppNS.ProjectType): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getPermitByIdStart());
      const idsArray = ids.split(',').map((id: string) => {
        return id.trim().toLowerCase();
      });

      const permits = await apiGetPermitsById(uniq(idsArray), jobType);

      dispatch(getPermitByIdSuccess(permits));
    } catch (err: any) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err);
      dispatch(getPermitByIdFailed());
    }
  };

export const addReportDocument =
  (inspectionEventId: string, inspectionId: string, imageInfo: FileInfo): AppThunk =>
  async (dispatch) => {
    try {
      const output = await apiAddReportDocument({
        properties: {
          type: 'event_inspection',
          eventInspectionId: inspectionEventId,
          inspectionId: inspectionId,
          file: imageInfo.url || '',
        },
      });
      dispatch(
        addFile({
          inspectionEventId,
          file: { ...output, uppyId: imageInfo.uppyId },
        })
      );
    } catch (err: any) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err?.toString());
    }
  };

export const removeReportDocument =
  (inspectionId: string, imageId: string): AppThunk =>
  async (dispatch) => {
    try {
      await apiDeleteReportDocument(imageId);
      dispatch(removeImageSuccess({ inspectionId, imageId }));
    } catch (err: any) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err?.toString());
    }
  };

export const saveInspectionQuestionnaire =
  (id: string, docs: AppNS.Params): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setInspectionQuestionnaireStart());
      const docsUpdated = await apiSaveInspectionQuestionnaire(id, docs);
      dispatch(setInspectionQuestionnaireSuccess(docsUpdated));
    } catch (err: any) {
      if (err) {
        dispatch(setInspectionQuestionnaireFailed(err));
      }
    }
  };

export const saveInspectionChecklist =
  (id: string, inspectionChecklist: AppNS.Params): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setInspectionChecklistStart());
      const results = await apiSaveReportChecklist(id, inspectionChecklist);
      dispatch(setInspectionChecklistSuccess(results));
    } catch (err: any) {
      dispatch(setInspectionChecklistFailed(err as string[]));
    }
  };

export const updateReportInspection =
  (
    id: string,
    notes: string,
    state: AppNS.InspectionStatus,
    failedCodes: string[],
    customFailedCodes: string,
    gsComments: string,
    recordedAmperage: string,
    electricalSupplementalType: string
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setInspectionUpdateStart(id));
      await apiUpdateReportDcraInspection(
        id,
        notes,
        state,
        failedCodes,
        customFailedCodes,
        gsComments,
        recordedAmperage,
        electricalSupplementalType
      );
      dispatch(
        setInspectionUpdateSuccess({
          id,
          notes,
          state,
          failedCodes,
          customFailedCodes,
          gsComments,
          recordedAmperage,
          electricalSupplementalType,
        })
      );
    } catch (err: any) {
      dispatch(setInspectionUpdateFailed(err as string[]));
    }
  };

export const submitReport =
  (report: Dcra.Inspection): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setFinalizeStart());
      const result = await apiSubmitReport(report);
      dispatch(setSubmitSuccess(result));
      dispatch(setDetailsActionDone(true));
    } catch (err: any) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      dispatch(setFinalizeFailed(err as AppNS.Params));
    }
  };

export const getPermitsHistoryById =
  (id: string): AppThunk =>
  async (dispatch) => {
    dispatch(getPermitHistoryStart(id));
    await apiGetPermitHistory(id);
  };

export const setPermitsHistoryById =
  (id: string, history: AppNS.PermitHistoryItem[]): AppThunk =>
  async (dispatch) => {
    dispatch(getPermitsHistorySuccess({ id, history }));
  };

export const setPermitsHistoryTimeout =
  (id: string): AppThunk =>
  async (dispatch) => {
    dispatch(getPermitsHistoryTimeoutFailed(id));
  };

export const getHistoryFilesById =
  (permitId: string, id: string): AppThunk =>
  async (dispatch) => {
    dispatch(getHistoryFilesStart(id));
    await apiGetPermitHistoryFilesList(permitId, id);
  };

export const setHistoryFilesById =
  (id: string, files: AppNS.Params[]): AppThunk =>
  async (dispatch) => {
    dispatch(getHistoryFilesSuccess({ id, files }));
  };

export const setHistoryFilesTimeout =
  (id: string): AppThunk =>
  async (dispatch) => {
    dispatch(getHistoryFilesTimeoutFailed(id));
  };

export const getHistoryFile =
  (permitId: string, fileInfo: HistoryFileItem): AppThunk =>
  async (dispatch) => {
    dispatch(getDownloadFileStart(fileInfo));
    await apiGetPermitHistoryFile(permitId, fileInfo.id);
  };

export const setHistoryFileDownloadById =
  (id: string, doc: string): AppThunk =>
  async (dispatch) => {
    dispatch(getDownloadFileSuccess({ id, doc }));
  };

export const setHistoryFileDownloadTimeout =
  (id: string): AppThunk =>
  async (dispatch) => {
    dispatch(getDownloadFileTimeoutFailed(id));
  };

export const unsetHistoryFileDownloadTimeout =
  (id: string): AppThunk =>
  async (dispatch) => {
    dispatch(resetDownloadFileTimeout(id));
  };
